'use client';

import { FC, useCallback, useEffect, useState } from 'react';
import { shallow } from 'zustand/shallow';

import { liveChatStore } from 'src/stores/liveChatStore';

export const Chat: FC = () => {
  const [canInitWidget, setCanInitWidget] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { visibility, setLiveChatVisibility } = liveChatStore(
    ({ visibility, setLiveChatVisibility }) => ({
      visibility,
      setLiveChatVisibility,
    }),
    shallow,
  );

  const initChatWidget = useCallback(() => {
    setCanInitWidget(true);
  }, []);

  useEffect(() => {
    if (canInitWidget && !isInitialized && visibility !== 'hidden') {
      globalThis.window.LiveChatWidget?.init();
      setIsInitialized(true);
    }
  }, [canInitWidget, visibility, isInitialized]);

  const stateChangeEvent = (event: Event) => {
    if (event.target instanceof Document && event.target.readyState === 'complete') {
      initChatWidget();
    }
  };

  // Delay the livechat widget loading as much as possible
  useEffect(() => {
    document.addEventListener('readystatechange', stateChangeEvent);

    if (document.readyState === 'complete') {
      initChatWidget();
    }

    return () => {
      document.removeEventListener('readystatechange', stateChangeEvent);
    };
  }, []);

  useEffect(() => {
    window.LiveChatWidget?.call(getVisibility(visibility));
  }, [visibility]);

  useEffect(() => {
    const handleVisibilityChange = (data: { visibility: 'minimized' | 'maximized' | 'hidden' }) => {
      setLiveChatVisibility(data.visibility);
    };

    window.LiveChatWidget?.on('visibility_changed', handleVisibilityChange);

    return () => {
      window.LiveChatWidget?.off('visibility_changed', handleVisibilityChange);
    };
  }, [setLiveChatVisibility]);

  return null;
};

const getVisibility = (visibility?: string) => {
  switch (visibility) {
    case 'hidden':
      return 'hide';
    case 'minimized':
      return 'minimize';
    case 'maximized':
      return 'maximize';
    default:
      return 'hide';
  }
};
